import React, { createContext, useContext, useEffect } from "react"
import { navigate } from "@reach/router"

import { API_ENDPOINT } from "../declarations"

import { useAuthenticationContext } from "./AuthenticationContext"
import { useMaintenanceContext } from "./MaintenanceContext"

type AuthenticatedContextInterface = {
	authenticated: boolean
}
const AuthenticatedContext =
	createContext<AuthenticatedContextInterface | null>(null)

export const useAuthenticatedContext = () => {
	const ctx = useContext(AuthenticatedContext)

	if (ctx === null) {
		throw new Error("No AuthenticatedContextProvider available.")
	}

	return ctx
}

export const AuthenticatedContextProvider: React.FC = ({ children }) => {
	const { authenticated, logout } = useAuthenticationContext(true)
	const { setMaintenance } = useMaintenanceContext()

	useEffect(() => {
		if (!authenticated) {
			return
		}

		const ping = async () => {
			const response = await fetch(`${API_ENDPOINT}/users/ping`, {
				credentials: "include",
			})

			if (response.status === 401) {
				await logout()
			}

			if (response.status === 503) {
				const date = await response.text()
				setMaintenance(date.length > 0 ? date : true)
			}
		}

		ping().then()
		const interval = setInterval(ping, 45000)

		return () => {
			clearInterval(interval)
		}
	}, [authenticated, logout, setMaintenance])

	if (!authenticated) {
		navigate("/login")
		return null
	}

	const ctx = {
		authenticated,
	}

	return (
		<AuthenticatedContext.Provider value={ctx}>
			{children}
		</AuthenticatedContext.Provider>
	)
}
